import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Mail, Edit3 } from 'lucide-react'
import { Button, Card } from '../../components/ui'
import { authService } from '../../services/authService'
import { useRegistrationStore } from '../../stores/registrationStore'
import { ProgressStepper } from '../../components/onboarding/ProgressStepper'

const RESEND_COOLDOWN = 60000 // 1 minute
const MAX_RESEND_ATTEMPTS = 3

const EmailVerificationWaitingPage: React.FC = () => {
  const {
    registrationData,
    clearRegistrationData
  } = useRegistrationStore()
  const [resendCount, setResendCount] = useState(0)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const cooldownRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // Redirect if no registration data
    if (!registrationData || !registrationData.email) {
      window.location.href = '/pricing'
      return
    }

    return () => {
      if (cooldownRef.current) {
        clearInterval(cooldownRef.current)
      }
    }
  }, [])

  const handleResendEmail = async () => {
    if (
      !registrationData?.email ||
      resendCount >= MAX_RESEND_ATTEMPTS ||
      resendCooldown > 0
    ) {
      return
    }

    setIsResending(true)
    setError(null)

    try {
      const response = await authService.resendVerificationEmail(
        registrationData.email
      )

      if (response.success) {
        setResendCount((prev) => prev + 1)
        setResendCooldown(RESEND_COOLDOWN)

        // Start cooldown timer
        const cooldownInterval = setInterval(() => {
          setResendCooldown((prev) => {
            const newValue = prev - 1000
            if (newValue <= 0) {
              clearInterval(cooldownInterval)
              return 0
            }
            return newValue
          })
        }, 1000)

        cooldownRef.current = cooldownInterval
      } else {
        setError(
          response.error?.message || 'Failed to resend verification email'
        )
      }
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : 'Failed to resend verification email'
      )
    } finally {
      setIsResending(false)
    }
  }

  const handleEditEmail = () => {
    // Return to registration page
    clearRegistrationData()
    window.location.href = '/register'
  }

  const steps = [
    { label: 'Account', description: 'Create account' },
    { label: 'Verify Email', description: 'Confirm email' },
    { label: 'Payment', description: 'Enter payment' },
    { label: 'Complete', description: 'Start trial' }
  ]

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50'>
      <div className='container mx-auto px-4 py-6 sm:py-8'>
        <ProgressStepper steps={steps} currentStep={1} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className='max-w-xl mx-auto'
        >
          <Card className='p-5 sm:p-6'>
            <div className='flex justify-center mb-4'>
              <motion.div
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
                className='w-14 h-14 bg-avi-primary bg-opacity-10 rounded-full flex items-center justify-center'
              >
                <Mail className='w-7 h-7 text-avi-primary' />
              </motion.div>
            </div>

            <h1 className='text-xl sm:text-2xl font-bold text-center text-gray-900 mb-3'>
              Check Your Email
            </h1>

            <div className='bg-gray-50 rounded-lg p-3 mb-4 border border-gray-200'>
              <p className='text-center text-sm text-gray-900 font-medium break-all'>
                {registrationData?.email}
              </p>
            </div>

            <p className='text-center text-sm text-gray-600 mb-5'>
              We've sent a verification link to your email address. Please click
              the link to verify your account and continue with your free trial
              setup.
            </p>


            {/* Error Message */}
            {error && (
              <div className='p-3 bg-red-50 border border-red-200 rounded-lg mb-4'>
                <p className='text-red-600 text-xs sm:text-sm text-center'>{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className='space-y-2'>
              <Button
                variant='primary'
                size='md'
                onClick={handleResendEmail}
                disabled={
                  isResending ||
                  resendCount >= MAX_RESEND_ATTEMPTS ||
                  resendCooldown > 0
                }
                loading={isResending}
                className='w-full text-sm'
              >
                {resendCooldown > 0
                  ? `Resend Available in ${Math.ceil(resendCooldown / 1000)}s`
                  : resendCount >= MAX_RESEND_ATTEMPTS
                  ? 'Maximum Resend Attempts Reached'
                  : 'Resend Verification Email'}
              </Button>

              <Button
                variant='outline'
                size='md'
                onClick={handleEditEmail}
                disabled={isResending}
                className='w-full text-sm'
              >
                <Edit3 className='w-3.5 h-3.5 mr-1.5' />
                Change Email Address
              </Button>
            </div>

            {/* Resend Counter */}
            {resendCount > 0 && (
              <p className='text-[10px] sm:text-xs text-center text-gray-500 mt-3'>
                Resend attempts: {resendCount} / {MAX_RESEND_ATTEMPTS}
              </p>
            )}

            {/* Help Text */}
            <div className='mt-5 pt-4 border-t border-gray-200'>
              <p className='text-xs sm:text-sm text-gray-600 text-center mb-1.5'>
                Didn't receive the email?
              </p>
              <ul className='text-[10px] sm:text-xs text-gray-500 space-y-0.5'>
                <li>• Check your spam or junk folder</li>
                <li>• Make sure {registrationData?.email} is correct</li>
                <li>• Wait a few minutes and try resending</li>
              </ul>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}

export default EmailVerificationWaitingPage
